import { Copy, Folder, FolderInput, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { FolderItemType } from "src/components/Folders/types";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import type { ActionItem } from "src/components/NewComponents/menuActions/types";
import { useRemoveFolderItems } from "src/hooks/useFolders";

interface RowWithFolderContext {
	id: number | string;
	folderId?: string;
	folderItemId?: number | string;
	[key: string]: unknown;
}

export const useFolderItemActions = (
	row: RowWithFolderContext,
	itemType: FolderItemType,
): ActionItem => {
	const { t } = useTranslation();
	const { openModal } = useModals();
	const { mutateAsync: removeItems } = useRemoveFolderItems();

	const selectedItem = {
		id: String(row.folderItemId),
		itemId: String(row.id),
		itemType,
	};

	const moveToFolder = () => {
		openModal(ModalsNames.MoveToFolderModal, {
			folderId: row.folderId,
			selectedItems: [selectedItem],
			isCopyMode: false,
			onSuccess: () => {
				toast.success(
					t("folder.movedToFolderSuccess", "Przeniesiono do folderu"),
				);
			},
		});
	};

	const copyToFolder = () => {
		openModal(ModalsNames.MoveToFolderModal, {
			selectedItems: [selectedItem],
			isCopyMode: true,
			multiSelect: true,
			onSuccess: () => {
				toast.success(
					t("folder.copiedToFolderSuccess", "Skopiowano do folderu"),
				);
			},
		});
	};

	const removeFromFolder = () => {
		if (!row.folderId) return;
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.removeFromFolder.title", "Usunąć z folderu?"),
			description: t(
				"modals.deleteItem.removeFromFolder.description",
				"Wybrane elementy zostaną usunięte z folderu, ale nie zostaną trwale usunięte. Nadal będziesz mieć do nich dostęp.",
			),
			confirmText: t(
				"modals.deleteItem.removeFromFolder.confirm",
				"Usuń z folderu",
			),
			onDelete: async () => {
				await removeItems({
					folderId: row.folderId,
					items: [Number(row.folderItemId)],
				});
				toast.success(
					t("folder.removedFromFolderSuccess", "Usunięto z folderu"),
				);
			},
		});
	};

	return {
		title: t("menuActions.folder.folders", "Foldery"),
		Icon: <Folder className="icon-small" />,
		items: [
			{
				title: t(
					"menuActions.folder.moveToFolder",
					"Przenieś do innego folderu",
				),
				onClick: moveToFolder,
				Icon: <FolderInput className="icon-small" />,
			},
			{
				title: t("menuActions.folder.copyToFolder", "Kopiuj do folderu"),
				onClick: copyToFolder,
				Icon: <Copy className="icon-small" />,
			},
			{
				title: t("menuActions.folder.removeFromFolder", "Usuń z folderu"),
				className: "list-item-error",
				onClick: removeFromFolder,
				Icon: <Trash2 className="icon-small" />,
			},
		],
	};
};
